'use client';

import { AddToCartRequest, CartItemViewModel } from '@/types/api';
import { SupportedEvents, sendAddOrRemoveCartItemEvent } from '@/features/analytics/datalayer';
import { Button } from '@/features/button';
import usePostAddToCart from '@/features/product-card/utils/use-post-add-to-cart';
import { Webengage } from '@/features/webengage';
import { SupportedEvents as WESupportedEvents } from '@/features/webengage/types';
import { CartActionPlacement } from '@/features/webengage/types';
import { useGetProductInCart } from '@/app/(pdp)/_utils/use-get-product-in-cart';
import usePDPData from '@/app/(pdp)/_utils/use-pdp-data';
import { useAppCart } from '@/app/_utils/use-app-cart';
import { Binoban } from '@/utils/binoban';
import { cn } from '@/utils/cn';
interface Props {
  maxLimited: number;
  className?: string;
  atpId: AddToCartRequest['atpId'];
  itemProposalId?: AddToCartRequest['itemProposalId'];
  openAlsoBoughtModal: () => void;
  showVariantErrorHandler: () => void;
  type: CartActionPlacement.pdp | CartActionPlacement.video;
  moveScrollToTopOnVariantError?: boolean;
}
export const AddToCartButton = ({
  className,
  atpId,
  itemProposalId,
  openAlsoBoughtModal,
  showVariantErrorHandler,
  type,
  moveScrollToTopOnVariantError = true
}: Props) => {
  const {
    data
  } = usePDPData();
  const {
    updateCart,
    cartInStorage: prevCart
  } = useAppCart();
  const {
    isLoading: isLoadingCart
  } = useGetProductInCart(`${atpId}`);
  const {
    mutateAsync: mutateAddToCart,
    isPending: isPendingAddToCart
  } = usePostAddToCart({
    atpId: atpId,
    itemProposalId: itemProposalId || ''
  });
  const webengageAddToCartHandler = (addedItem: CartItemViewModel) => {
    Webengage.trackCartAction(addedItem ?? {}, type, WESupportedEvents.add_to_cart, addedItem?.pricingGift ?? false);
  };
  const addToCartHandler = async () => {
    if (!itemProposalId) {
      showVariantErrorHandler();
      moveScrollToTopOnVariantError && scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    const cart = await mutateAddToCart();
    updateCart(cart);
    if (prevCart) {
      sendAddOrRemoveCartItemEvent(SupportedEvents.add_to_cart, prevCart, cart, type);
    }
    openAlsoBoughtModal();
    const addedProduct = cart.items?.find((value: CartItemViewModel) => value.atpId === atpId);
    const addedProducts = cart?.items?.filter(nextItem => !prevCart?.items?.some(currentItem => currentItem.id === nextItem.id));
    if (addedProduct?.gift) {
      prevCart && addedProducts?.forEach(addedItem => {
        webengageAddToCartHandler(addedItem);
      });
    } else {
      webengageAddToCartHandler(addedProduct ?? {});
    }
    Binoban.productAddedEvent(data?.id || '', data?.breadcrumb?.at(-1)?.slug || '', `${addedProduct?.vendorId ?? ''}-${atpId}`, addedProduct?.vendorId ?? '');
  };
  return <div className={cn('flex-col gap-2 lg:h-auto lg:w-full', className)} data-sentry-component="AddToCartButton" data-sentry-source-file="add-to-cart-button.tsx">
            <Button graphicStyle='filled' size='large' onClick={addToCartHandler} isLoading={isLoadingCart || isPendingAddToCart} className='w-full whitespace-nowrap' data-sentry-element="Button" data-sentry-source-file="add-to-cart-button.tsx">
                افزودن به سبد خرید
            </Button>
        </div>;
};